import React, { useState } from "react";
import { Modal, DataTable as Table, Badge, Avatar, Button } from "@/components/ui";
import toast from "react-hot-toast";
import {
  Download,
  Upload,
  X,
  Check,
  AlertTriangle,
  FileSpreadsheet,
} from "lucide-react";
import Papa from "papaparse";
import * as XLSX from "xlsx";

import { downloadCustomerTemplate } from "../utils/templateUtils";
import { processImportRows } from "../utils/importUtils";

export const BulkUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  existingCustomers = [],
  initialFile,
}) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (isOpen && initialFile) {
      setFile(initialFile);
      parseFile(initialFile);
    }
  }, [isOpen, initialFile]);

  const parseFile = (f) => {
    const isExcel = f.name.endsWith(".xlsx") || f.name.endsWith(".xls");

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          defval: "",
        });
        const { validRows, errors: errs } = processImportRows(rows, existingCustomers);
        setPreview(validRows);
        setErrors(errs);
      };
      reader.readAsArrayBuffer(f);
    } else {
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const { validRows, errors: errs } = processImportRows(results.data, existingCustomers);
          setPreview(validRows);
          setErrors(errs);
        },
        error: () => {
          toast.error("Failed to parse CSV file");
          handleCancel();
        },
      });
    }
  };

  const handleCancel = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
  };

  const doUpload = async () => {
    if (errors.length > 0 && preview.length === 0) {
      toast.error("No valid entries to upload.");
      return;
    }

    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));

    try {
      if (preview.length > 0) {
        const res = await onUpload(preview, file?.name || "bulk_upload.xlsx");
        
        if (res && res.failed > 0) {
          // Merge backend errors into the errors state to display them
          setErrors(prev => [...prev, ...res.errors]);
          setPreview([]); // Clear preview so user focuses on fixing errors
          toast.error(`Processed ${res.success} successfully, but ${res.failed} failed.`);
        } else {
          handleCancel();
          onClose();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Bulk Customer Upload"
      size="max-w-4xl"
    >
      <div className="space-y-4 fade-in">
        {file && (
          <div className="flex items-center justify-between bg-blue-50 p-4 rounded-md border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-md shadow-sm font-bold text-brand-blue capitalize">
                {file.name.split(".").pop()}
              </div>
              <div>
                <div className="font-bold text-sm text-brand-dark">
                  {file.name}
                </div>
                <div className="text-[10px] text-slate-500">
                  {(file.size / 1024).toFixed(1)} KB • Extracted{" "}
                  {preview.length + errors.length} total rows
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                handleCancel();
                onClose();
              }}
              className="text-slate-400 hover:text-red-500 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        )}

        {errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center gap-2 text-red-700 font-bold mb-3">
              <AlertTriangle size={18} /> Found {errors.length} rows with
              format errors
            </div>
            <div className="max-h-[160px] overflow-y-auto space-y-2 pr-2 custom-scrollbar text-[11px] text-red-600">
              {errors.map((err, idx) => (
                <div
                  key={idx}
                  className="flex items-start justify-between gap-3 border-b border-red-100 pb-2 last:border-0"
                >
                  <div className="flex gap-3">
                    <span className="font-semibold w-16 shrink-0">
                      Row {err.row}:
                    </span>
                    <div>
                      {err.errors.map((msg, i) => (
                        <p key={i}>• {msg}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-slate-100 rounded-md overflow-hidden shadow-sm bg-white">
          <div className="bg-slate-50 px-4 py-2 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Valid Entries Preview ({preview.length})
            </span>
            {preview.length > 0 && (
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                Ready to Process
              </span>
            )}
          </div>
          {preview.length > 0 ? (
            <Table
              headers={["Customer Name", "Phone Number", "City", "Status"]}
              maxHeight="max-h-[300px]"
            >
              {preview.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="td p-3">
                    <div className="flex items-center gap-2">
                      <Avatar name={p.customerName} size="w-6 h-6 text-[10px]" />
                      <span className="text-xs font-semibold text-brand-dark">
                        {p.customerName}
                      </span>
                    </div>
                  </td>
                  <td className="td p-3 text-xs">{p.phoneNumber}</td>
                  <td className="td p-3 text-xs">{p.city}</td>
                  <td className="td p-3">
                    <span className="text-xs font-bold text-green-600">
                      Ready
                    </span>
                  </td>
                </tr>
              ))}
            </Table>
          ) : (
            <div className="p-6 text-center text-slate-500 text-sm">
              No valid customers found in this file. Please fix errors and
              re-upload.
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100">
          <Button
            disabled={loading}
            onClick={() => {
              handleCancel();
              onClose();
            }}
            variant="secondary"
            className="flex-1 py-3"
          >
            Cancel
          </Button>
          <Button
            disabled={loading || preview.length === 0}
            onClick={doUpload}
            variant="primary"
            className="flex-1 py-3"
            loading={loading}
            leftIcon={!loading && <Check size={18} />}
          >
            Process {preview.length} Customers
          </Button>
        </div>
      </div>
    </Modal>
  );
};
